import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import type { SortDirection } from '@/types';

// Table Root
interface TableProps {
  children: ReactNode;
  className?: string;
}

export function Table({ children, className }: TableProps) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn('w-full', className)}>{children}</table>
    </div>
  );
}

// Table Header
interface TableHeaderProps {
  children: ReactNode;
  className?: string;
}

export function TableHeader({ children, className }: TableHeaderProps) {
  return (
    <thead className={cn('bg-slate-50 border-b border-slate-200', className)}>
      {children}
    </thead>
  );
}

// Table Body
interface TableBodyProps {
  children: ReactNode;
  className?: string;
}

export function TableBody({ children, className }: TableBodyProps) {
  return <tbody className={cn('divide-y divide-slate-100', className)}>{children}</tbody>;
}

// Table Row
interface TableRowProps {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  isSelected?: boolean;
}

export function TableRow({ children, className, onClick, isSelected }: TableRowProps) {
  return (
    <tr
      className={cn(
        'transition-colors',
        onClick && 'cursor-pointer hover:bg-slate-50',
        isSelected && 'bg-telvia-50',
        className
      )}
      onClick={onClick}
    >
      {children}
    </tr>
  );
}

// Table Head Cell
interface TableHeadProps {
  children?: ReactNode;
  className?: string;
  sortable?: boolean;
  sortDirection?: SortDirection | null;
  onSort?: () => void;
  align?: 'left' | 'center' | 'right';
}

export function TableHead({
  children,
  className,
  sortable = false,
  sortDirection,
  onSort,
  align = 'left',
}: TableHeadProps) {
  const alignments = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  return (
    <th
      className={cn(
        'px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider',
        alignments[align],
        sortable && 'cursor-pointer select-none hover:bg-slate-100 transition-colors',
        className
      )}
      onClick={sortable ? onSort : undefined}
    >
      <div className={cn('flex items-center gap-1', align === 'right' && 'justify-end')}>
        {children}
        {sortable && (
          <span className="text-slate-400">
            {sortDirection === 'asc' ? (
              <ChevronUp className="h-4 w-4" />
            ) : sortDirection === 'desc' ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronsUpDown className="h-3.5 w-3.5" />
            )}
          </span>
        )}
      </div>
    </th>
  );
}

// Table Cell
interface TableCellProps {
  children?: ReactNode;
  className?: string;
  align?: 'left' | 'center' | 'right';
}

export function TableCell({ children, className, align = 'left' }: TableCellProps) {
  const alignments = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  return (
    <td className={cn('px-4 py-3 text-sm text-slate-700', alignments[align], className)}>
      {children}
    </td>
  );
}

// Empty State
interface TableEmptyProps {
  message?: string;
  icon?: ReactNode;
  colSpan: number;
}

export function TableEmpty({
  message = 'No records to display',
  icon,
  colSpan,
}: TableEmptyProps) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-12 text-center">
        <div className="flex flex-col items-center gap-2 text-slate-400">
          {icon}
          <p className="text-sm">{message}</p>
        </div>
      </td>
    </tr>
  );
}

// Loading State
interface TableLoadingProps {
  colSpan: number;
  rows?: number;
}

export function TableLoading({ colSpan, rows = 5 }: TableLoadingProps) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="animate-pulse">
          <td colSpan={colSpan} className="px-4 py-3">
            <div className="h-4 bg-slate-200 rounded w-full" />
          </td>
        </tr>
      ))}
    </>
  );
}

// Attach sub-components
Table.Header = TableHeader;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Head = TableHead;
Table.Cell = TableCell;
Table.Empty = TableEmpty;
Table.Loading = TableLoading;
